import React, { useState, useRef, useEffect } from 'react';
import { Message } from '../types';
import { getVibeBotResponse } from '../services/geminiService';
import { MessageSquare, Send, X, ChevronDown } from 'lucide-react';

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Welcome. I am Vibe, your conversational guide. How can I help you navigate our mission or the upcoming Salon?",
      sender: 'bot',
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, isMinimized]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    const history = messages.map(m => ({
      role: m.sender === 'user' ? 'user' as const : 'model' as const,
      parts: [{ text: m.text }]
    }));

    const botReply = await getVibeBotResponse(input, history);
    
    const botMsg: Message = {
      id: (Date.now() + 1).toString(),
      text: botReply,
      sender: 'bot',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, botMsg]);
    setIsLoading(false);
  };

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className="fixed bottom-8 right-8 bg-terracotta text-white p-5 rounded-full shadow-[0_10px_40px_-10px_rgba(192,108,93,0.5)] hover:scale-110 hover:shadow-[0_15px_50px_-10px_rgba(192,108,93,0.6)] transition-all duration-300 z-50 flex items-center gap-3 border border-white/20 active:scale-95"
      >
        <MessageSquare size={20} />
        <span className="hidden md:inline text-[10px] font-black uppercase tracking-[0.3em]">Talk to Vibe</span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-8 right-8 w-80 md:w-96 bg-white border border-neutral-200 rounded-xl shadow-[0_35px_60px_-15px_rgba(0,0,0,0.3)] z-50 overflow-hidden transition-all duration-500 ease-in-out ${
        isMinimized ? 'h-[64px]' : 'h-[550px]'
      }`}
    >
      {/* Header Container */}
      <div className="bg-charcoal h-[64px] flex items-center justify-between px-4 text-white select-none border-b border-white/5">
        
        {/* LEFT: Minimize Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized(!isMinimized);
          }} 
          className="w-10 h-10 flex items-center justify-center hover:bg-white/10 rounded-full transition-all text-neutral-300 hover:text-white"
          title={isMinimized ? "Expand" : "Minimize"}
        >
          <ChevronDown 
            size={22} 
            className={`transition-transform duration-500 ${isMinimized ? 'rotate-180' : 'rotate-0'}`} 
          />
        </button>

        {/* CENTER: Branding */}
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-terracotta rounded-full animate-pulse shadow-[0_0_10px_rgba(192,108,93,1)]" />
          <span className="text-[10px] font-black uppercase tracking-[0.4em] opacity-90">Vibe Guide</span>
        </div>
        
        {/* RIGHT: Close Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(false);
          }} 
          className="w-10 h-10 flex items-center justify-center hover:bg-white/10 rounded-full transition-all text-neutral-300 hover:text-white"
          title="Close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Chat Area & Input - Only visible when not minimized */}
      <div className={`flex flex-col h-[calc(550px-64px)] transition-opacity duration-300 ${isMinimized ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div 
          ref={scrollRef} 
          className="flex-1 p-6 overflow-y-auto space-y-6 bg-cream/30 scroll-smooth"
        >
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed font-medium shadow-sm border ${
                msg.sender === 'user' 
                  ? 'bg-terracotta text-white border-terracotta/20 rounded-tr-none' 
                  : 'bg-white text-charcoal border-neutral-100 rounded-tl-none'
              }`}>
                {msg.text}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-neutral-100 p-4 rounded-2xl rounded-tl-none flex gap-1.5 items-center shadow-sm">
                <div className="w-1.5 h-1.5 bg-terracotta rounded-full animate-bounce [animation-delay:-0.3s]" />
                <div className="w-1.5 h-1.5 bg-terracotta rounded-full animate-bounce [animation-delay:-0.15s]" />
                <div className="w-1.5 h-1.5 bg-terracotta rounded-full animate-bounce" />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-white border-t border-neutral-100">
          <div className="flex gap-2 bg-neutral-50 p-1.5 rounded-xl border border-neutral-200">
            <input 
              type="text" 
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="SAY SOMETHING..."
              className="flex-1 text-[10px] font-bold tracking-widest bg-transparent border-none px-3 py-2 focus:ring-0 outline-none uppercase placeholder:text-neutral-300"
            />
            <button 
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="bg-charcoal text-white w-10 h-10 flex items-center justify-center rounded-lg disabled:opacity-20 hover:bg-terracotta transition-all shadow-sm active:scale-90"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;